import React, { forwardRef } from 'react'
import {
  Box,
  Heading,
  Text,
  Divider,
  VStack,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td
} from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import { type Prescription } from './types'
import { prescriptionPreviewMessages } from './messages'

interface PrescriptionPreviewProps {
  prescription: Prescription
  doctorName: string
}

const PrescriptionPreview = forwardRef<
  HTMLDivElement,
  PrescriptionPreviewProps
>((props, ref) => {
  const { prescription, doctorName } = props
  const { formatMessage } = useIntl()
  const formattedDate = new Date(prescription.date).toLocaleDateString(
    'en-US',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }
  )

  return (
    <Box
      ref={ref}
      p={8}
      bg="white"
      maxWidth="800px"
      mx="auto"
      className="prescription-preview"
    >
      <VStack spacing={5} align="stretch">
        {/* Header */}
        <HStack justifyContent="space-between">
          <Box>
            <Heading as="h1" size="lg" color="teal.600">
              {formatMessage(prescriptionPreviewMessages.title)}
            </Heading>
            <Text fontSize="md" color="gray.600">
              {formattedDate}
            </Text>
          </Box>
          <Box textAlign="right">
            <Text fontWeight="bold">Dr. {doctorName}</Text>
          </Box>
        </HStack>

        <Divider borderColor="gray.300" />

        {/* Patient Information */}
        <Box>
          <Heading as="h2" size="md" mb={2}>
            {formatMessage(prescriptionPreviewMessages.patientInfo)}
          </Heading>
          <Text>{formatMessage(prescriptionPreviewMessages.nameLabel)} {prescription.patientName}</Text>
        </Box>

        <Divider borderColor="gray.300" />

        {/* Medications */}
        <Box>
          <Heading as="h2" size="md" mb={4}>
            {formatMessage(prescriptionPreviewMessages.prescribedMedications)}
          </Heading>
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>{formatMessage(prescriptionPreviewMessages.numberHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.medicationHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.concentrationHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.quantityHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.frequencyHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.durationHeader)}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {prescription.medications.map((med, idx) => (
                <Tr key={med.id}>
                  <Td>{idx + 1}</Td>
                  <Td>{med.name}</Td>
                  <Td>{med.concentration}</Td>
                  <Td>{med.quantity}</Td>
                  <Td>{med.frequency}</Td>
                  <Td>{med.duration}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>

        <Box mt={4}>
          <Heading as="h3" size="sm" mb={2}>
            {formatMessage(prescriptionPreviewMessages.specialInstructions)}
          </Heading>
          <Table variant="simple" size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>{formatMessage(prescriptionPreviewMessages.numberHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.medicationHeader)}</Th>
                <Th>{formatMessage(prescriptionPreviewMessages.instructionsHeader)}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {prescription.medications
                .filter((med) => med.instructions)
                .map((med, idx) => (
                  <Tr key={med.id}>
                    <Td>{idx + 1}</Td>
                    <Td>{med.name}</Td>
                    <Td>{med.instructions}</Td>
                  </Tr>
                ))}
            </Tbody>
          </Table>
        </Box>

        {prescription.doctorNotes && (
          <Box mt={4}>
            <Heading as="h3" size="sm" mb={2}>
              {formatMessage(prescriptionPreviewMessages.doctorNotes)}
            </Heading>
            <Box p={3} borderWidth="1px" borderRadius="md">
              <Text>{prescription.doctorNotes}</Text>
            </Box>
          </Box>
        )}

        <Divider borderColor="gray.300" mt={6} />

        {/* Signature */}
        <Box mt={4} textAlign="right">
          <Text fontWeight="bold">Dr. {doctorName}</Text>
          <Box
            width="150px"
            height="40px"
            mt={2}
            borderBottom="1px solid"
            borderColor="gray.400"
            display="inline-block"
          />
          <Text mt={1}>{formatMessage(prescriptionPreviewMessages.signature)}</Text>
        </Box>
      </VStack>
    </Box>
  )
})

PrescriptionPreview.displayName = 'PrescriptionPreview'

export default PrescriptionPreview
